import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Newspaper comic data from the backend.
 */
interface NewspaperComic {
    id: number;
    title: string | null;
    slug: string;
    artist_name: string;
    artist_url: string | null;
    thumbnail_url: string | null;
    images_count: number;
    is_published: boolean;
    published_at: string | null;
    created_by: string | null;
    created_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated comics response.
 */
interface PaginatedComics {
    data: NewspaperComic[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Comics Index page.
 */
interface Props {
    comics: PaginatedComics;
    filters: {
        search: string | null;
        published: string | null;
    };
}

/**
 * GM Comics Index page - displays all newspaper comics with management actions.
 */
export default function Index({ comics, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [publishedFilter, setPublishedFilter] = useState(filters.published || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/newspaper/comics', {
            search: search || undefined,
            published: publishedFilter || undefined,
        }, { preserveState: true });
    }, [search, publishedFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setPublishedFilter('');
        router.get('/gm/newspaper/comics', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (comic: NewspaperComic) => {
        const title = comic.title || `Feature by ${comic.artist_name}`;
        if (confirm(`Are you sure you want to delete "${title}"?`)) {
            router.delete(`/gm/newspaper/comics/${comic.id}`, { preserveScroll: true });
        }
    };

    // Handle toggle publish
    const handleTogglePublish = (comic: NewspaperComic) => {
        router.patch(`/gm/newspaper/comics/${comic.id}/toggle-publish`, {}, { preserveScroll: true });
    };

    return (
        <GmLayout title="Features" activePath="/gm/newspaper">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">🎨 Features</h1>
                    <div className="flex gap-2">
                        <Link
                            href="/gm/newspaper"
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            ← Back to Newspaper
                        </Link>
                        <Link
                            href="/gm/newspaper/comics/create"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                        >
                            + New Comic
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Title or artist name"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Published Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={publishedFilter}
                                onChange={(e) => setPublishedFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="true">Published</option>
                                <option value="false">Draft</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Comics Grid */}
                {comics.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No Features found. Create your first comic to start publishing.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {comics.data.map((comic) => (
                            <ComicCard
                                key={comic.id}
                                comic={comic}
                                onDelete={handleDelete}
                                onTogglePublish={handleTogglePublish}
                            />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {comics.last_page > 1 && (
                    <Pagination links={comics.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * ComicCard - Displays a single comic card with actions.
 */
function ComicCard({
    comic,
    onDelete,
    onTogglePublish,
}: {
    comic: NewspaperComic;
    onDelete: (comic: NewspaperComic) => void;
    onTogglePublish: (comic: NewspaperComic) => void;
}) {
    const displayTitle = comic.title || `Feature by ${comic.artist_name}`;

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            {/* Thumbnail */}
            <div className="aspect-square bg-slate-700 relative">
                {comic.thumbnail_url ? (
                    <img
                        src={comic.thumbnail_url}
                        alt={displayTitle}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                        No thumbnail
                    </div>
                )}
                {/* Status Badge */}
                <span className={`absolute top-2 right-2 px-2 py-1 text-xs rounded ${comic.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                    {comic.is_published ? 'Published' : 'Draft'}
                </span>
            </div>

            {/* Info */}
            <div className="p-4">
                <Link href={`/gm/newspaper/comics/${comic.id}/edit`} className="text-white font-medium hover:text-purple-400 block truncate">
                    {displayTitle}
                </Link>
                <p className="text-sm text-gray-400 mt-1">by {comic.artist_name}</p>
                <p className="text-xs text-gray-500 mt-1">{comic.images_count} image{comic.images_count !== 1 ? 's' : ''}</p>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                    <Link
                        href={`/gm/newspaper/comics/${comic.id}/edit`}
                        className="flex-1 px-3 py-1 text-xs text-center bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={() => onTogglePublish(comic)}
                        className={`flex-1 px-3 py-1 text-xs rounded transition-colors ${
                            comic.is_published
                                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                    >
                        {comic.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                        onClick={() => onDelete(comic)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

